import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ChevronDown, Sparkles } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const HERO_IMG =
  'https://images.unsplash.com/photo-1718209881007-c0ecdfc00f9d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920';

export const HeroCinematic = () => {
  const { isDark } = useTheme();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const scale = useTransform(scrollYProgress, [0, 1], [1.15, 1]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 180]);

  return (
    <section ref={ref} data-testid="lhf-hero" className="relative h-[100svh] overflow-hidden">
      <motion.div style={{ scale }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Selbstbewusster Mann mit vollem Haar nach Long Hair FUE Haartransplantation ohne Rasur"
          className="w-full h-full object-cover object-[center_15%]"
        />
        <div
          className={`absolute inset-0 ${
            isDark
              ? 'bg-gradient-to-b from-slate-950/70 via-slate-950/40 to-slate-950'
              : 'bg-gradient-to-b from-slate-900/60 via-slate-900/30 to-slate-50'
          }`}
        />
      </motion.div>

      <motion.div
        style={{ opacity, y: textY }}
        className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6"
      >
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="inline-flex items-center gap-2 px-5 py-2 rounded-full backdrop-blur-xl bg-white/10 border border-white/20 text-white text-sm mb-8"
        >
          <Sparkles className="w-4 h-4 text-[#9BBFD9]" />
          Premium-Methode der Dion Hair Clinic
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4 }}
          className="text-white text-5xl sm:text-6xl lg:text-8xl font-extralight tracking-tight leading-none"
          style={{ fontFamily: "'Montserrat', sans-serif" }}
        >
          Long Hair <span className="text-[#9BBFD9]">FUE</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.7 }}
          className="mt-6 max-w-2xl text-white/80 text-base md:text-lg font-light"
        >
          Die Haartransplantation komplett ohne Rasur. Ihre Haare bleiben in voller Länge —
          niemand muss wissen, dass Sie bei uns waren.
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="absolute bottom-10 flex flex-col items-center gap-2 text-white/60"
        >
          <span className="text-xs uppercase tracking-[0.3em]">Entdecken</span>
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
            <ChevronDown className="w-5 h-5" />
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
};

const LINES = [
  { text: 'Sie wünschen sich volles Haar.', accent: false },
  { text: 'Aber der Gedanke an einen rasierten Kopf hält Sie zurück?', accent: false },
  { text: 'Wochenlang sichtbare Spuren. Neugierige Blicke. Fragen im Büro.', accent: false },
  { text: 'Es geht auch anders.', accent: true },
];

export const ProblemStory = () => {
  const { isDark } = useTheme();
  return (
    <section
      data-testid="lhf-problem"
      className={`relative py-32 md:py-48 px-6 ${isDark ? 'bg-slate-950' : 'bg-slate-50'}`}
    >
      <div className="max-w-3xl mx-auto space-y-12 md:space-y-16">
        {LINES.map((line, i) => (
          <motion.p
            key={i}
            initial={{ opacity: 0, y: 40, filter: 'blur(6px)' }}
            whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            viewport={{ once: true, margin: '-20%' }}
            transition={{ duration: 0.9, ease: 'easeOut' }}
            className={`font-extralight leading-snug ${
              line.accent
                ? 'text-4xl sm:text-5xl lg:text-6xl bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] bg-clip-text text-transparent'
                : `text-2xl sm:text-3xl lg:text-4xl ${isDark ? 'text-slate-300' : 'text-slate-700'}`
            }`}
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            {line.text}
          </motion.p>
        ))}
      </div>
    </section>
  );
};
